import { RootState } from '../rootState'
import { CmsPageState, initialCmsPageState } from '../cmsPage/cmsPageState'
import { getCmsPage, getHomePage, getPreview } from '../cmsPage/sharepointPageApi/sharepointPageApi'
import { initialNotFoundState, NotFoundState } from '../notFound/notFoundState'
import { initialSearchState, SearchState } from '../search/searchState'
import { CourseSearchState, initialCourseSearchState } from '../courseSearch/courseSearchState'
import { CourseDateScheduleState, initialCourseDateScheduleState } from '../courseDateSchedule/courseDateScheduleState'
import { initialMailPlusFormState, MailPlusFormState } from '../mailPlusForm/mailPlusFormState'
import { EmptyState, initialEmptyState } from '../empty/emptyState'
import { initialKnowledgeSearchState, KnowledgeSearchState } from '../knowledgeSearch/knowledgeSearchState'
import { ActualitySearchState, initialActualitySearchState } from '../actualitySearch/actualitySearchState'
import { RouteBuilders, RouteUpdaters } from '../../utils/routes'
import { initialMagazinesOverviewState, MagazinesOverviewPageState } from '../magazinesOverview/magazinesOverviewState'
import { EducationFormState, initialEducationFormState } from '../educationForm/educationFormState'
import { EventFormState, initialEventFormState } from '../eventForm/eventForm.state'
import {
  initialOpleidingenOverviewState,
  OpleidingenOverviewPageState,
} from '../opleidingenOverview/opleidingenOverviewState'

type SlugParam = { slug: string }

export const pageParamToPage = (page?: string) => {
  const pageParamInt = page ? parseInt(page) : NaN
  return isNaN(pageParamInt) ? 1 : pageParamInt
}

export type Pages = {
  mailPlusForm: {
    url: [{ formId: string }]
    pageState: MailPlusFormState
  }

  educationForm: {
    url: [{ eventId: string }]
    pageState: EducationFormState
  }

  eventForm: {
    url: [{ eventId: string }]
    pageState: EventFormState
  }

  courseDateSchedule: {
    url: [{ opl: string; ref: string }]
    pageState: CourseDateScheduleState
  }

  courseSearch: {
    url: [{ search?: string; page?: string }]
    pageState: CourseSearchState
  }

  actualitySearch: {
    url: [{ search?: string; page?: string }]
    pageState: ActualitySearchState
  }

  knowledgeSearch: {
    url: [{ search?: string; page?: string }]
    pageState: KnowledgeSearchState
  }

  search: {
    url: [{ search?: string; page?: string }]
    pageState: SearchState
  }

  opleidingenOverview: {
    url: [{ search?: string; page?: string; filters?: string }]
    pageState: OpleidingenOverviewPageState
  }

  magazinesOverviewPage: {
    url: [{ page?: string }]
    pageState: MagazinesOverviewPageState
  }

  notFound: {
    url: []
    pageState: NotFoundState
  }
  empty: {
    url: []
    pageState: EmptyState
  }

  home: {
    url: [SlugParam, { from?: string }]
    pageState: CmsPageState
  }

  cmsPage: {
    url: [SlugParam, { from?: string }, { page?: number }]
    pageState: CmsPageState
  }

  previewPage: {
    url: [SlugParam, { token: string }, { page?: number }]
    pageState: CmsPageState
  }
}

export const routeBuilders: RouteBuilders<RootState, Pages> = {
  mailPlusForm: {
    make: (params) => ({
      kind: 'mailPlusForm',
      params: params,
      pageState: initialMailPlusFormState(params.formId),
    }),
  },
  educationForm: {
    make: (params) => ({
      kind: 'educationForm',
      params: params,
      pageState: initialEducationFormState(params.eventId),
    }),
  },
  eventForm: {
    make: (params) => ({
      kind: 'eventForm',
      params: params,
      pageState: initialEventFormState(params.eventId),
    }),
  },
  courseDateSchedule: {
    make: (params) => ({
      kind: 'courseDateSchedule',
      params: params,
      pageState: initialCourseDateScheduleState(params.opl, params.ref),
    }),
  },
  courseSearch: {
    make: (params) => ({
      kind: 'courseSearch',
      params: params,
      pageState: initialCourseSearchState(params.search, params.page),
    }),
  },
  actualitySearch: {
    make: (params) => ({
      kind: 'actualitySearch',
      params: params,
      pageState: initialActualitySearchState(params.search, params.page),
    }),
  },
  knowledgeSearch: {
    make: (params) => ({
      kind: 'knowledgeSearch',
      params: params,
      pageState: initialKnowledgeSearchState(params.search, params.page),
    }),
  },
  search: {
    make: (params) => ({
      kind: 'search',
      params: params,
      pageState: initialSearchState(params.search, params.page),
    }),
  },
  opleidingenOverview: {
    make: (params) => ({
      kind: 'opleidingenOverview',
      params: params,
      pageState: initialOpleidingenOverviewState(params.filters ?? '', params.search ?? '', params.page ?? '1'),
    }),
  },
  magazinesOverviewPage: {
    make: (params) => ({
      kind: 'magazinesOverviewPage',
      params: params,
      pageState: initialMagazinesOverviewState(params.page),
    }),
  },
  notFound: {
    make: (params) => ({
      kind: 'notFound',
      params: params,
      pageState: initialNotFoundState,
    }),
  },
  empty: {
    make: (params) => ({
      kind: 'empty',
      params: params,
      pageState: initialEmptyState,
    }),
  },
  home: {
    make: (params) => ({
      kind: 'home',
      params: params,
      pageState: initialCmsPageState(() => getHomePage()),
    }),
  },
  cmsPage: {
    make: (params) => ({
      kind: 'cmsPage',
      params: params,
      pageState: initialCmsPageState(() => getCmsPage(params.slug)),
    }),
  },
  previewPage: {
    make: (params) => ({
      kind: 'previewPage',
      params: params,
      pageState: initialCmsPageState(() => getPreview(params.slug, params.token)),
    }),
  },
}

export const routeUpdaters: RouteUpdaters<RootState, Pages> = {
  mailPlusForm: {
    jumpTo:
      (params) =>
      (s0: RootState): RootState => ({
        ...s0,
        page: routeBuilders.mailPlusForm.make(params),
      }),
    url: '/formulieren/:formId',
  },
  opleidingenOverview: {
    jumpTo:
      (params) =>
      (s0: RootState): RootState => ({
        ...s0,
        page: routeBuilders.opleidingenOverview.make(params),
      }),
    url: '/opleidingen',
  },
  educationForm: {
    jumpTo:
      (params) =>
      (s0: RootState): RootState => ({
        ...s0,
        page: routeBuilders.educationForm.make(params),
      }),
    url: '/opleidingen/inschrijven',
  },
  eventForm: {
    jumpTo:
      (params) =>
      (s0: RootState): RootState => ({
        ...s0,
        page: routeBuilders.eventForm.make(params),
      }),
    url: '/evenementen/inschrijven',
  },
  courseDateSchedule: {
    jumpTo:
      (params) =>
      (s0: RootState): RootState => ({
        ...s0,
        page: routeBuilders.courseDateSchedule.make(params),
      }),
    url: '/opleidingen/rooster',
  },
  courseSearch: {
    jumpTo:
      (params) =>
      (s0: RootState): RootState => ({
        ...s0,
        page: routeBuilders.courseSearch.make(params),
      }),
    url: '/opleidingen/zoeken',
  },
  actualitySearch: {
    jumpTo:
      (params) =>
      (s0: RootState): RootState => ({
        ...s0,
        page: routeBuilders.actualitySearch.make(params),
      }),
    url: '/actualiteiten/zoeken',
  },
  knowledgeSearch: {
    jumpTo:
      (params) =>
      (s0: RootState): RootState => ({
        ...s0,
        page: routeBuilders.knowledgeSearch.make(params),
      }),
    url: '/kennis/zoeken',
  },
  search: {
    jumpTo:
      (params) =>
      (s0: RootState): RootState => ({
        ...s0,
        page: routeBuilders.search.make(params),
      }),
    url: '/zoeken',
  },
  magazinesOverviewPage: {
    jumpTo:
      (params) =>
      (s0: RootState): RootState => ({
        ...s0,
        page: routeBuilders.magazinesOverviewPage.make(params),
      }),
    url: '/magazines',
  },
  notFound: {
    jumpTo:
      (params) =>
      (s0: RootState): RootState => ({
        ...s0,
        page: routeBuilders.notFound.make(params),
      }),
    url: '',
  },
  empty: {
    jumpTo:
      (params) =>
      (s0: RootState): RootState => ({
        ...s0,
        page: routeBuilders.empty.make(params),
      }),
    url: '',
  },
  home: {
    jumpTo:
      (params) =>
      (s0: RootState): RootState => ({
        ...s0,
        page: routeBuilders.home.make(params),
      }),
    url: '/',
  },
  previewPage: {
    jumpTo:
      (params) =>
      (s0: RootState): RootState => ({
        ...s0,
        page: routeBuilders.previewPage.make(params),
      }),
    url: '/preview/:slug',
  },
  cmsPage: {
    jumpTo:
      (params) =>
      (s0: RootState): RootState => ({
        ...s0,
        page: routeBuilders.cmsPage.make(params),
      }),
    url: '(/:parents*)/:slug',
  },
}
